import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type DashboardMode = 'standard' | 'cfo';

const KEY = 'fint:dashboard:mode';

/**
 * Which face the Dashboard shows: the one it has always had, or CFO mode.
 *
 * ── STANDARD UNLESS SOMEONE CHOSE OTHERWISE ──
 * A person who never touches the switch sees exactly the Dashboard they
 * had; the choice is remembered per browser, as the Overview's month view
 * and the breakdown cards remember theirs, and `?mode=cfo` in a link opens
 * CFO mode without changing what this browser remembers. Storage can be
 * missing or refuse a write (a private window, a blocked site); either way
 * the answer is Standard and nothing throws.
 */
export function useDashboardMode(): [DashboardMode, (mode: DashboardMode) => void] {
  const [params, setParams] = useSearchParams();
  const [stored, setStored] = useState<DashboardMode>(() => read());
  const fromUrl = params.get('mode');
  const mode: DashboardMode = fromUrl === 'cfo' || fromUrl === 'standard' ? fromUrl : stored;

  const setMode = useCallback(
    (next: DashboardMode) => {
      setStored(next);
      try {
        localStorage.setItem(KEY, next);
      } catch {
        // Not remembered; still switched for this visit.
      }
      if (params.has('mode')) {
        const copy = new URLSearchParams(params);
        copy.delete('mode');
        setParams(copy, { replace: true });
      }
    },
    [params, setParams],
  );

  return [mode, setMode];
}

function read(): DashboardMode {
  try {
    return localStorage.getItem(KEY) === 'cfo' ? 'cfo' : 'standard';
  } catch {
    return 'standard';
  }
}

/** The two-way switch, in the pill style of the basis toggle beside it on the same page. */
export function DashboardModeToggle({ value, onChange }: { value: DashboardMode; onChange: (mode: DashboardMode) => void }) {
  return (
    <div role="group" aria-label="Dashboard view" className="flex gap-1 rounded-full border border-border p-0.5">
      {(
        [
          ['standard', 'Standard'],
          ['cfo', 'CFO'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={
            value === key
              ? 'inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11'
              : 'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted active:bg-muted touch:min-h-11'
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}
